/**
 * BYOR import UI (§4.8.1, FR-46 – FR-49).
 *
 * Paste modes come first and are presented first, on purpose. On a hosted tool
 * they are not a fallback — they are the safest and most reliable path (§4.7),
 * because the content never leaves the device, cannot be bot-blocked, and works
 * with both Workers disabled. The known-recipe quick-add section and generic
 * URL mode both come after, and both disappear entirely when
 * `ENABLE_URL_FETCH=false` (FR-71) — a quick-add is just a URL fetch with the
 * URL filled in from a bundled recipe (`./knownRecipes.ts`), never a separate
 * fetch path or trust tier.
 *
 * The preview is **mandatory and non-skippable on first import** (FR-48). There
 * is no code path in this file that commits a source without one, and the
 * commit button stays disabled until the user has actually seen it.
 *
 * Everything extracted is rendered with `text()` into the tool's own table.
 * Source markup is never inserted into the DOM (FR-56).
 */

import { el, clear, text, fmt } from './dom.ts';
import type { Store } from '../state/store.ts';
import { importFromPaste, commitImport, fetchUrlContent, replayRecipe, UrlFetchDisabledError } from '../sources/byor.ts';
import type { CandidateBlock, ColumnRole, ExtractionPreview } from '../core/byor/extract.ts';
import { extractRankings } from '../core/byor/extract.ts';
import { buildRecipe, recipeToYaml, type Recipe } from '../core/byor/recipes.ts';
import { KNOWN_RECIPES } from './knownRecipes.ts';
import { normalizeName } from '../core/matching.ts';
import { mountTurnstile, type TurnstileController } from './turnstile.ts';

const COLUMN_ROLES: ColumnRole[] = ['rank', 'name', 'team', 'position', 'tier', 'bye', 'notes', 'ignore'];

export interface ByorModalOptions {
  urlFetchEnabled: boolean;
  /** Null on deployments that don't require a challenge for URL mode (FR-74). */
  turnstileSiteKey: string | null;
  onClose: () => void;
}

export function renderByorModal(host: HTMLElement, store: Store, opts: ByorModalOptions): void {
  clear(host);

  const state = store.get();
  const index = state.index;
  if (!index) return;

  let block: CandidateBlock | null = null;
  let preview: ExtractionPreview | null = null;
  let sourceUrl: string | null = null;
  const dropped = new Set<number>();
  const overrides = new Map<number, string>();

  // FR-74: URL mode only, and only on deployments that configured a widget.
  let turnstileToken: string | null = null;
  let turnstileController: TurnstileController | null = null;

  const nameInput = el('input', {
    type: 'text',
    class: 'input',
    placeholder: 'e.g. Hayden Winks (Yahoo) top 300',
  }) as HTMLInputElement;

  const urlInput = el('input', {
    type: 'url',
    class: 'input',
    placeholder: 'https://…',
  }) as HTMLInputElement;

  const pasteArea = el('textarea', {
    class: 'paste-area',
    rows: '10',
    placeholder:
      '1. Jahmyr Gibbs DET\n2. Ja’Marr Chase CIN\n…or paste a spreadsheet, a table, or the page as it looks on screen. Any of them work.',
  }) as HTMLTextAreaElement;

  const status = el('p', { class: 'byor-status', role: 'status', 'aria-live': 'polite' });
  const previewHost = el('div', { class: 'byor-preview' });
  const userSourcesHost = el('div', { class: 'byor-sources' });

  const commitButton = el('button', {
    class: 'btn btn-primary',
    type: 'button',
    disabled: true,
  }, 'Add to my board') as HTMLButtonElement;

  const setStatus = (message: string, kind: 'info' | 'error' | 'good' = 'info') => {
    clear(status);
    status.className = `byor-status ${kind}`;
    status.appendChild(text(message));
  };

  // -------------------------------------------------------------------------
  // Manage previously-added sources
  //
  // `kind: 'user'` is the only marker that matters here — built-in sources
  // (Sleeper, FFC ADP, FantasyCalc) refetch on their own and have no "remove"
  // affordance; only what the user pasted or fetched themselves shows up.
  // These now survive a reload (persisted to IndexedDB by the store), so a
  // way to remove one is no longer optional.
  // -------------------------------------------------------------------------

  const renderUserSourcesList = () => {
    clear(userSourcesHost);
    const userSources = store.get().sources.filter((s) => s.kind === 'user');
    if (userSources.length === 0) return;

    userSourcesHost.appendChild(el('label', {}, 'Your rankings'));
    const list = el('div', { class: 'byor-source-list' });
    for (const source of userSources) {
      list.appendChild(
        el(
          'div',
          { class: 'byor-source-row' },
          el(
            'span',
            { class: 'byor-source-meta' },
            el('span', { class: 'byor-source-name' }, source.source_name),
            ` — ${source.rows.length} players · added ${source.as_of_date}`,
          ),
          el(
            'button',
            {
              class: 'btn btn-quiet btn-small',
              type: 'button',
              'aria-label': `Remove ${source.source_name}`,
              onClick: () => {
                store.removeSource(source.source_id);
                renderUserSourcesList();
              },
            },
            'Remove',
          ),
        ),
      );
    }
    userSourcesHost.appendChild(list);
    userSourcesHost.appendChild(
      el(
        'p',
        { class: 'hint' },
        'Re-adding a source under the same name replaces it, so pasting an updated list works without removing the old one first.',
      ),
    );
  };
  renderUserSourcesList();

  // -------------------------------------------------------------------------
  // Extraction + preview
  // -------------------------------------------------------------------------

  const runExtraction = (content: string) => {
    if (!content.trim()) {
      setStatus('Paste a list first.', 'error');
      return;
    }

    dropped.clear();
    overrides.clear();

    const outcome = importFromPaste(
      {
        content,
        source_name: nameInput.value.trim() || 'My rankings',
        threshold: state.config.engine.extraction_confidence_threshold,
        matchThreshold: state.config.engine.match_threshold,
      },
      index,
    );

    block = outcome.result.best;
    preview = outcome.preview;

    if (!block || !preview) {
      setStatus(
        'Could not find a ranking list in that. If this came from a webpage, try copying the page as it looks on screen instead of "View Page Source" — some sites load their rankings with JavaScript, which page source (and a URL fetch) never sees. Otherwise, try selecting just the list itself, or paste it as plain numbered lines.',
        'error',
      );
      clear(previewHost);
      commitButton.disabled = true;
      return;
    }

    // FR-46: a candidate below threshold is never silently accepted. It is
    // still shown, because the user can often fix it by remapping a column —
    // but the commit button stays off until they do.
    if (preview.below_threshold) {
      setStatus(
        `Only ${fmt.pct(preview.confidence)} of ${preview.total} rows matched a real NFL player, below the ${fmt.pct(outcome.result.threshold)} threshold. Check the column mapping below before adding this.`,
        'error',
      );
    } else {
      setStatus(
        `Matched ${preview.matched} of ${preview.total} rows (${fmt.pct(preview.confidence)}). Review below, then add it.`,
        'good',
      );
    }

    renderPreview();
    commitButton.disabled = false;
  };

  const renderPreview = () => {
    clear(previewHost);
    if (!block || !preview) return;

    previewHost.appendChild(
      el(
        'div',
        { class: 'preview-summary' },
        el('span', {}, `${preview.total} rows`),
        el('span', {}, `${preview.matched} matched`),
        el(
          'span',
          { class: preview.below_threshold ? 'bad' : 'good' },
          `${fmt.pct(preview.confidence)} confidence`,
        ),
        el('span', {}, `via ${preview.strategy}`),
      ),
    );

    // Column remapping — the single most useful correction, so it is the most
    // prominent control rather than buried in an advanced section.
    const mapper = el('div', { class: 'column-mapper' });
    preview.columns.forEach((role, i) => {
      const select = el('select', {
        class: 'column-select',
        onChange: (event: Event) => {
          const value = (event.target as HTMLSelectElement).value as ColumnRole;
          const columns = [...preview!.columns];
          columns[i] = value;
          reExtractWithColumns(columns);
        },
      }) as HTMLSelectElement;

      for (const option of COLUMN_ROLES) {
        select.appendChild(el('option', { value: option, selected: option === role }, option));
      }
      mapper.appendChild(el('label', { class: 'column-label' }, `Col ${i + 1}`, select));
    });
    previewHost.appendChild(el('h4', {}, 'Columns (inferred from content, not headers)'));
    previewHost.appendChild(mapper);

    previewHost.appendChild(el('h4', {}, `First ${preview.head.length} rows`));
    previewHost.appendChild(renderRowTable(preview.head, 0));

    if (preview.tail.length > 0 && preview.total > preview.head.length) {
      previewHost.appendChild(el('h4', {}, `Last ${preview.tail.length} rows`));
      previewHost.appendChild(renderRowTable(preview.tail, preview.total - preview.tail.length));
    }

    // FR-48 + FR-49: every unmatched name, with fuzzy suggestions and a
    // one-click resolution that persists to the alias file.
    if (preview.unmatched.length > 0) {
      previewHost.appendChild(
        el('h4', { class: 'warn' }, `${preview.unmatched.length} names did not match a player`),
      );
      const list = el('div', { class: 'unmatched-list' });

      for (const row of preview.unmatched) {
        const rowIndex = block!.rows.indexOf(row);
        const controls = el('div', { class: 'unmatched-actions' });

        for (const suggestion of row.suggestions.slice(0, 3)) {
          controls.appendChild(
            el(
              'button',
              {
                class: 'btn btn-small',
                type: 'button',
                onClick: () => {
                  overrides.set(rowIndex, suggestion.player_key);
                  // Persist the correction so it applies to every future import.
                  store.addAlias(normalizeName(row.name), suggestion.player_key);
                  setStatus(`Mapped "${row.name}" → ${suggestion.full_name}. Saved for next time.`, 'good');
                  (controls.parentElement as HTMLElement | null)?.classList.add('resolved');
                },
              },
              `${suggestion.full_name} (${fmt.pct(suggestion.score)})`,
            ),
          );
        }

        controls.appendChild(
          el(
            'button',
            {
              class: 'btn btn-small btn-quiet',
              type: 'button',
              onClick: () => {
                dropped.add(rowIndex);
                setStatus(`Dropped "${row.name}".`);
              },
            },
            'Drop',
          ),
        );

        list.appendChild(
          el(
            'div',
            { class: 'unmatched-row' },
            el('span', { class: 'unmatched-name' }, `#${row.rank} ${row.name}`),
            controls,
          ),
        );
      }
      previewHost.appendChild(list);
    }
  };

  const reExtractWithColumns = (columns: ColumnRole[]) => {
    const content = pasteArea.value;
    if (!content.trim() || !block) return;
    const result = extractRankings(content, index, {
      columns,
      strategy: block.strategy,
      strategyIndex: block.index,
      threshold: state.config.engine.extraction_confidence_threshold,
      matchThreshold: state.config.engine.match_threshold,
    });
    if (!result.best) return;
    block = result.best;
    preview = {
      strategy: block.strategy,
      confidence: block.confidence,
      matched: block.matched,
      total: block.total,
      columns: block.columns,
      head: block.rows.slice(0, 25),
      tail: block.rows.slice(-10),
      unmatched: block.rows.filter((r) => !r.player_key),
      below_threshold: block.confidence < result.threshold,
    };
    setStatus(`Remapped — ${preview.matched}/${preview.total} matched (${fmt.pct(preview.confidence)}).`);
    renderPreview();
  };

  // -------------------------------------------------------------------------
  // Quick-add from a bundled recipe (§4.8.3)
  //
  // Same trust tier and same fetch mechanism as URL mode below — this just
  // saves typing a known URL. The preview it lands on is the ordinary one,
  // still gated behind an explicit "Add to my board" click (FR-48); a recipe
  // whose baseline no longer matches (`needs-reconfirm`) is surfaced as a
  // warning here rather than silently accepted or silently refused.
  // -------------------------------------------------------------------------

  const runQuickAdd = async (recipe: Recipe) => {
    if (opts.turnstileSiteKey && !turnstileToken) {
      setStatus('Complete verification below, then try the quick-add again.', 'error');
      return;
    }

    nameInput.value = recipe.source_name;
    setStatus(`Fetching ${recipe.source_name}…`);

    try {
      const outcome = await replayRecipe(recipe, index, { turnstileToken });

      if (!outcome.result.best || !outcome.preview) {
        setStatus(
          `Could not read ${recipe.source_name} right now. Try again shortly, or paste the page instead.`,
          'error',
        );
        return;
      }

      block = outcome.result.best;
      preview = outcome.preview;
      sourceUrl = outcome.resolved_url;

      if (outcome.check.verdict !== 'ok') {
        setStatus(
          `${recipe.source_name} loaded, but it may have changed shape since this recipe was written: ${outcome.check.reasons.join(' ')} Check the columns below before adding it.`,
          'error',
        );
      } else if (preview.below_threshold) {
        setStatus(
          `Only ${fmt.pct(preview.confidence)} of ${preview.total} rows matched a real NFL player. Check the column mapping below before adding this.`,
          'error',
        );
      } else {
        setStatus(
          `Matched ${preview.matched} of ${preview.total} rows (${fmt.pct(preview.confidence)}). Review below, then add it.`,
          'good',
        );
      }

      renderPreview();
      commitButton.disabled = false;
    } catch (err) {
      setStatus(
        err instanceof UrlFetchDisabledError
          ? err.message
          : `${err instanceof Error ? err.message : 'Fetch failed.'} Paste the page instead.`,
        'error',
      );
    } finally {
      turnstileController?.reset();
    }
  };

  const renderRowTable = (rows: ExtractionPreview['head'], offset: number): HTMLElement => {
    const table = el('table', { class: 'preview-table' });
    table.appendChild(
      el(
        'thead',
        {},
        el(
          'tr',
          {},
          el('th', {}, '#'),
          el('th', {}, 'Player'),
          el('th', {}, 'Pos'),
          el('th', {}, 'Team'),
          el('th', {}, 'Matched'),
        ),
      ),
    );
    const body = el('tbody');
    rows.forEach((row, i) => {
      const matchedPlayer = row.player_key ? state.universe?.get(row.player_key) : undefined;
      body.appendChild(
        el(
          'tr',
          { class: row.player_key ? '' : 'unmatched' },
          // Every cell is a text node — never markup from the source (FR-56).
          el('td', {}, text(String(row.rank))),
          el('td', {}, text(row.name)),
          el('td', {}, text(row.position ?? '')),
          el('td', {}, text(row.team ?? '')),
          el('td', {}, text(matchedPlayer?.full_name ?? '—')),
        ),
      );
    });
    table.appendChild(body);
    return table;
  };

  // -------------------------------------------------------------------------
  // Commit
  // -------------------------------------------------------------------------

  commitButton.onclick = () => {
    if (!block) return;
    const name = nameInput.value.trim() || 'My rankings';
    const slug = slugify(name);

    const source = commitImport(block, {
      source_id: slug,
      source_name: name,
      source_url: sourceUrl,
      dropped,
      overrides,
      positionOf: (id) => store.get().universe?.get(id)?.position ?? null,
    });

    if (source.rows.length === 0) {
      setStatus('Nothing left to add after drops — every row was unmatched.', 'error');
      return;
    }

    store.addSource(source);
    opts.onClose();
  };

  // -------------------------------------------------------------------------
  // Layout
  // -------------------------------------------------------------------------

  const pasteTab = el(
    'section',
    { class: 'byor-tab active' },
    el('label', {}, 'What is this list called?'),
    nameInput,
    el('label', {}, 'Paste it here'),
    pasteArea,
    el(
      'p',
      { class: 'hint' },
      'Plain text, a spreadsheet copy, or an HTML table all work. Copying from a webpage? Copy the page as it looks on screen (select the list, or select-all once it has fully loaded) rather than "View Page Source" — many sites load their rankings with JavaScript after the page loads, so the raw source can be missing the list entirely even though it looks fine on screen. This runs entirely in your browser; the list never leaves your device.',
    ),
    el(
      'button',
      { class: 'btn', type: 'button', onClick: () => runExtraction(pasteArea.value) },
      'Read this list',
    ),
  );

  const fetchButton = el(
    'button',
    {
      class: 'btn',
      type: 'button',
      disabled: Boolean(opts.turnstileSiteKey),
      onClick: async () => {
        const url = urlInput.value.trim();
        if (!url) return;
        setStatus('Fetching…');
        try {
          const content = await fetchUrlContent({ url, turnstileToken });
          sourceUrl = url;
          pasteArea.value = content;
          runExtraction(content);
        } catch (err) {
          setStatus(
            err instanceof UrlFetchDisabledError
              ? err.message
              : `${err instanceof Error ? err.message : 'Fetch failed.'} Paste the page instead.`,
            'error',
          );
        } finally {
          // Each token is submitted at most once — get a fresh one for the
          // next attempt rather than letting a stale one look valid.
          turnstileController?.reset();
        }
      },
    },
    'Fetch and read',
  ) as HTMLButtonElement;

  const quickAddButtons: HTMLButtonElement[] = [];
  const turnstileHost = el('div', { class: 'turnstile-widget' });
  if (opts.turnstileSiteKey) {
    mountTurnstile(turnstileHost, opts.turnstileSiteKey, (token) => {
      turnstileToken = token;
      fetchButton.disabled = !token;
      quickAddButtons.forEach((b) => (b.disabled = !token));
    })
      .then((controller) => {
        turnstileController = controller;
      })
      .catch(() => {
        setStatus('Could not load verification. Use paste mode instead.', 'error');
      });
  }

  const knownSourcesTab = el(
    'section',
    { class: 'byor-tab known-recipes' },
    el('label', {}, 'Or add a known source in one click'),
    el(
      'div',
      { class: 'known-recipe-list' },
      ...KNOWN_RECIPES.map((recipe, i) => {
        const button = el(
          'button',
          {
            class: 'btn known-recipe-btn',
            type: 'button',
            disabled: Boolean(opts.turnstileSiteKey),
            onClick: () => runQuickAdd(recipe),
          },
          el('span', { class: 'known-recipe-name' }, recipe.source_name),
          i === 0 ? el('span', { class: 'known-recipe-badge' }, 'Suggested') : null,
        ) as HTMLButtonElement;
        quickAddButtons.push(button);
        return button;
      }),
    ),
    el(
      'p',
      { class: 'hint' },
      'Fetches that page right now, the same way URL mode below does — nothing is cached or shared with anyone else, and you still review it before it is added.',
    ),
  );

  const urlTab = el(
    'section',
    { class: 'byor-tab' },
    el('label', {}, 'Or fetch a URL'),
    urlInput,
    el(
      'p',
      { class: 'hint' },
      'Convenient, but two things can go wrong: large sites often block requests from cloud infrastructure (you’ll get a clear error), or the page loads its rankings with JavaScript, in which case this can come back 200 OK with nothing to find. Either way, open the page yourself, copy it as it looks on screen — not "View Page Source" — and paste it instead.',
    ),
    opts.turnstileSiteKey ? turnstileHost : null,
    fetchButton,
  );

  host.appendChild(
    el(
      'div',
      { class: 'modal byor-modal', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Add a ranking source' },
      el(
        'header',
        { class: 'modal-head' },
        el('h2', {}, 'Add your own rankings'),
        el('button', { class: 'btn btn-quiet', type: 'button', onClick: opts.onClose, 'aria-label': 'Close' }, '×'),
      ),
      userSourcesHost,
      pasteTab,
      opts.urlFetchEnabled ? knownSourcesTab : null,
      opts.urlFetchEnabled ? urlTab : null,
      status,
      previewHost,
      el(
        'footer',
        { class: 'modal-foot' },
        el('button', { class: 'btn btn-quiet', type: 'button', onClick: opts.onClose }, 'Cancel'),
        commitButton,
      ),
    ),
  );

  nameInput.focus();
}

/**
 * Export the current extraction as a shareable recipe (FR-52).
 *
 * Exposed separately from the modal so it can be wired to a menu item. The
 * output contains extraction instructions only — no player data — which is what
 * makes `recipes/community/` compatible with R2.
 */
export function exportRecipeYaml(
  block: CandidateBlock,
  args: { slug: string; source_name: string; url: string | null },
): string {
  return recipeToYaml(buildRecipe({ ...args, block }));
}

function slugify(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 48) || 'my-rankings'
  );
}
